<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    ref,
    watch,
  } from 'vue';
  import { ReservationsService } from '@/api/services/reservations.service';
  import V2ReservationTableGroupRow from '@/components/v2/reservations/tables/V2ReservationTableGroupRow.vue';
  import V2ReservationTableSelectionBar from '@/components/v2/reservations/tables/V2ReservationTableSelectionBar.vue';
  import V2ReservationTablesPlan from '@/components/v2/reservations/tables/V2ReservationTablesPlan.vue';
  import { useReservationGuide } from '@/composition/reservations/reservation-guide-context';
  import { useUi } from '@/composition/use-ui';
  import {
    effectiveOnlineMaxParty,
    maxBookableOnlineParty,
  } from '@/domain/reservations/reservation-capacity';
  import {
    GROUP_MAX_TABLES,
    GROUP_MIN_TABLES,
    defaultGroupRange,
    groupNameFor,
    groupSeatsTotal,
    joinableTableIds,
    raisedMaxParty,
  } from '@/domain/reservations/reservation-group-range';
  import type { IReservationGroupTableOption } from '@/domain/reservations/reservation-group-tables';
  import { isOnlineReservationTable } from '@/domain/reservations/reservation-tables';
  import { AlertType } from '@/interfaces/IAlert';
  import type { IReservationTableRef } from '@/interfaces/IReservation';
  import type { IReservationSettingsConfiguration } from '@/interfaces/IReservationSettings';

  /** Step 3 · Grupos grandes (optional): join tables on the plan and keep the combinations. */
  const context = useReservationGuide();
  const proxy = getCurrentInstance()!.proxy;
  const ui = useUi();
  const tr = (key: string, params?: Record<string, unknown>) =>
    String(proxy.$t(key, params));
  let alive = true;
  onBeforeUnmount(() => {
    alive = false;
  });
  const selectedIds = ref<number[]>([]);
  const activeSectorId = ref<number | null>(null);

  const configuration = computed(() => context.configuration.value);
  const busy = computed(() => context.busy.value);
  const tables = computed(() => context.tablesDraft.tables.value);
  const groups = computed(() => configuration.value?.tableGroups ?? []);
  const byId = computed(
    () => new Map(tables.value.map((table) => [table.id, table]))
  );
  const onlineSeated = computed(() =>
    tables.value.filter(
      (table) => isOnlineReservationTable(table) && table.capacity > 0
    )
  );
  const enough = computed(() => onlineSeated.value.length >= GROUP_MIN_TABLES);
  /** The largest single online table: what guests can book without joining. */
  const largestSingle = computed(() =>
    Math.max(0, ...onlineSeated.value.map((table) => table.capacity))
  );
  const selection = computed(() =>
    selectedIds.value
      .map((id) => byId.value.get(id))
      .filter((table): table is IReservationTableRef => table !== undefined)
  );
  const joinable = computed(() =>
    joinableTableIds(selectedIds.value, tables.value)
  );
  const joinEnabled = computed(
    () =>
      joinable.value.length >= GROUP_MIN_TABLES &&
      joinable.value.length <= GROUP_MAX_TABLES &&
      joinable.value.length === selectedIds.value.length
  );
  const joinSeats = computed(() =>
    groupSeatsTotal(selectedIds.value, tables.value)
  );
  const full = computed(() => selectedIds.value.length >= GROUP_MAX_TABLES);
  const groupedIds = computed(() =>
    groups.value.flatMap((group) => group.tableIds)
  );
  const bookable = computed(() =>
    maxBookableOnlineParty(tables.value, groups.value)
  );
  const counterMax = computed(() => {
    const settings = configuration.value?.settings;
    return settings
      ? effectiveOnlineMaxParty(
          settings.minPartyOnline,
          settings.maxPartyOnline,
          bookable.value
        )
      : 0;
  });
  /** «Permitir hasta {M}»: the tables seat more than the online maximum allows. */
  const raiseTo = computed(() => {
    const settings = configuration.value?.settings;
    return settings
      ? raisedMaxParty(settings.maxPartyOnline, bookable.value)
      : null;
  });
  const ready = computed(() => true);
  /** «Por ahora no» in the bar: only while joining is possible and nothing is joined yet. */
  const secondaryLabel = computed(() =>
    enough.value && groups.value.length === 0
      ? tr('reservations.guide.groups.skip')
      : null
  );

  function selectable(table: IReservationGroupTableOption): boolean {
    return (
      enough.value && isOnlineReservationTable(table) && table.capacity > 0
    );
  }
  function current(configurationAtStart: IReservationSettingsConfiguration) {
    const scope = context.scope();
    return (
      alive &&
      !!scope &&
      scope.restaurantId === configurationAtStart.settings.restaurantId &&
      scope.branchId === configurationAtStart.settings.branchId
    );
  }
  function toggle(id: number) {
    if (busy.value) return;
    selectedIds.value = selectedIds.value.includes(id)
      ? selectedIds.value.filter((value) => value !== id)
      : [...selectedIds.value, id];
  }
  function clearSelection() {
    selectedIds.value = [];
  }
  /** D6: creates the combination at once; a range above the online maximum raises it and says so. */
  async function join() {
    const cfg = configuration.value;
    if (!cfg || !joinEnabled.value || busy.value) return;
    const ids = [...selectedIds.value];
    const list = tables.value;
    const range = defaultGroupRange(ids, list);
    const result = await context.run(async () => {
      await ReservationsService.createTableGroup({
        restaurantId: cfg.settings.restaurantId,
        branchId: cfg.settings.branchId,
        name: groupNameFor(ids, list),
        tableIds: ids,
        minParty: range.minParty,
        maxParty: range.maxParty,
      });
      if (!current(cfg)) return false;
      const raised = raisedMaxParty(
        cfg.settings.maxPartyOnline,
        range.maxParty
      );
      // A failed raise keeps the group but is not a full success.
      let raisedOk = raised === null;
      if (raised !== null) {
        raisedOk = await context.savePatch({ maxPartyOnline: raised });
        if (raisedOk && current(cfg))
          ui.showToast(
            AlertType.Info,
            tr('reservations.guide.groups.raised', { max: raised })
          );
      }
      if (!(await context.reload())) return false;
      return raisedOk;
    }, tr('reservations.groups.error'));
    // null = the group was not created; false = created, but a follow-up write failed.
    if (result === null || !current(cfg)) return;
    clearSelection();
    if (result)
      ui.showToast(AlertType.Success, tr('reservations.groups.saved'));
  }
  async function raiseMax() {
    const raised = raiseTo.value;
    if (raised === null || busy.value) return;
    await context.savePatch({ maxPartyOnline: raised });
  }
  function reload() {
    void context.reload();
  }
  async function continueStep() {
    if (busy.value) return;
    context.next();
  }

  // A selected table that disappears (deleted elsewhere) leaves the selection.
  watch(tables, () => {
    const kept = selectedIds.value.filter((id) => byId.value.has(id));
    if (kept.length !== selectedIds.value.length) selectedIds.value = kept;
  });
  watch(
    () => configuration.value?.settings.branchId,
    () => {
      clearSelection();
      activeSectorId.value = null;
    }
  );

  defineExpose({ ready, continue: continueStep, secondaryLabel });
</script>

<template>
  <div class="reservations-guide-step-groups">
    <h2 class="step-title">{{ $t('reservations.guide.groups.title') }}</h2>
    <i18n
      v-if="enough"
      path="reservations.guide.groups.help"
      tag="p"
      class="step-help"
    >
      <template #max
        ><strong>{{ largestSingle }}</strong></template
      >
    </i18n>
    <p v-else class="step-help">
      {{ $t('reservations.guide.groups.needTwo') }}
    </p>
    <V2ReservationTablesPlan
      :areas="context.floorPlan.areas.value"
      :unplaced="context.floorPlan.unplaced.value"
      :active-sector-id="activeSectorId ?? undefined"
      :selected-ids="selectedIds"
      mode="groups"
      variant="guide"
      :grouped-ids="groupedIds"
      :selectable="selectable"
      :full="full"
      :disabled="busy"
      :status="context.floorPlan.status.value"
      @toggle="toggle"
      @area="activeSectorId = $event"
      @retry="context.floorPlan.load()"
    />
    <V2ReservationTableSelectionBar
      v-if="selection.length"
      :selection="selection"
      mode-control="toggle"
      mode="groups"
      :join-enabled="joinEnabled"
      :join-seats="joinSeats"
      :disabled="busy"
      @join="join"
      @done="clearSelection"
    />
    <section class="groups-card">
      <h3 class="groups-title">
        {{ $t('reservations.guide.groups.listTitle') }}
      </h3>
      <p v-if="!groups.length" class="groups-empty">
        {{ $t('reservations.guide.groups.empty') }}
      </p>
      <div v-else class="groups-list">
        <V2ReservationTableGroupRow
          v-for="group in groups"
          :key="group.id"
          class="group-item"
          :group="group"
          :tables="tables"
          :disabled="busy"
          :run="context.run"
          @saved="reload"
          @removed="reload"
        />
      </div>
    </section>
    <p class="counter-line">
      <i18n path="reservations.guide.groups.counter" tag="span">
        <template #max
          ><strong>{{ counterMax }}</strong></template
        >
      </i18n>
      <template v-if="raiseTo !== null">
        <span class="counter-separator" aria-hidden="true">·</span>
        <button
          type="button"
          class="raise-link"
          :disabled="busy"
          @click="raiseMax"
        >
          {{ $t('reservations.guide.groups.raiseMax', { max: raiseTo }) }}
        </button>
      </template>
      <span class="counter-rest">{{
        $t('reservations.guide.groups.counterRest')
      }}</span>
    </p>
  </div>
</template>

<style scoped lang="scss">
  .reservations-guide-step-groups {
    @apply tw-flex tw-flex-col tw-gap-4;
    p {
      @apply tw-m-0;
    }
    .step-title {
      @apply tw-m-0 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-neutral-900;
    }
    .step-help {
      @apply tw-text-sm tw-leading-relaxed tw-text-neutral-500;
      strong {
        @apply tw-font-semibold tw-text-neutral-900;
      }
    }
    .groups-card {
      @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200;
    }
    .groups-title {
      @apply tw-m-0 tw-border-0 tw-border-b tw-border-solid tw-border-neutral-200 tw-px-4 tw-py-3 tw-text-sm tw-font-semibold tw-text-neutral-900;
    }
    .groups-empty {
      @apply tw-px-4 tw-py-4 tw-text-sm tw-text-neutral-500;
    }
    .group-item {
      @apply tw-px-4 tw-py-4;
      & + .group-item {
        @apply tw-border-0 tw-border-t tw-border-solid tw-border-neutral-200;
      }
    }
    .counter-line {
      @apply tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-text-sm tw-text-neutral-600;
      strong {
        @apply tw-font-semibold tw-tabular-nums tw-text-neutral-900;
      }
      .counter-separator {
        @apply tw-text-neutral-400;
      }
      // Its own line after the maximum and the «Permitir hasta» link.
      .counter-rest {
        @apply tw-basis-full;
      }
    }
    .raise-link {
      @apply tw-inline-flex tw-min-h-11 tw-items-center tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-1 tw-text-sm tw-font-medium tw-text-neutral-900 tw-underline focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-orange-500/40;
      &:disabled {
        @apply tw-cursor-not-allowed tw-opacity-50;
      }
    }
  }
</style>
